import { apiHost } from '@pv/common/api';

const API_URL = apiHost(process.env.REACT_APP_PV_ENV || '');

interface AuthResponse {
  status: {
    code: number;
    message: string;
  };
  data?: {
    id: number;
    email: string;
    createdAt?: string;
    confirmed: boolean;
  };
  errors?: string[];
}

interface SignInParams {
  email: string;
  password: string;
}

interface SignUpParams extends SignInParams {
  first_name: string;
  last_name: string;
  phone: string;
  password_confirmation: string;
}

interface ResetPasswordParams {
  email: string;
}

interface UpdatePasswordParams {
  password: string;
  password_confirmation: string;
  reset_password_token: string;
}

class AuthService {
  private async fetch(url: string, options: RequestInit = {}) {
    const response = await fetch(`${API_URL}${url}`, {
      ...options,
      credentials: 'include', // Important for cookies
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const responseJson = await response.json();

      throw new Error(
        responseJson.errors?.[0] ||
          responseJson.status?.message ||
          'Authentication failed',
      );
    }

    return response.json();
  }

  async signIn({ email, password }: SignInParams): Promise<AuthResponse> {
    return this.fetch('/guests/sign_in', {
      method: 'POST',
      body: JSON.stringify({ guest: { email, password } }),
    });
  }

  async signUp({
    first_name,
    last_name,
    phone,
    email,
    password,
    password_confirmation,
  }: SignUpParams): Promise<AuthResponse> {
    return this.fetch('/guests', {
      method: 'POST',
      body: JSON.stringify({
        guest: {
          email,
          password,
          password_confirmation,
          first_name,
          last_name,
          phone,
        },
      }),
    });
  }

  async signOut(): Promise<AuthResponse> {
    return this.fetch('/guests/sign_out', {
      method: 'GET',
    });
  }

  async requestPasswordReset({
    email,
  }: ResetPasswordParams): Promise<AuthResponse> {
    return this.fetch('/guests/password', {
      method: 'POST',
      body: JSON.stringify({ guest: { email } }),
    });
  }

  async resetPassword({
    password,
    password_confirmation,
    reset_password_token,
  }: UpdatePasswordParams): Promise<AuthResponse> {
    return this.fetch('/guests/password', {
      method: 'PUT',
      body: JSON.stringify({
        guest: {
          password,
          password_confirmation,
          reset_password_token,
        },
      }),
    });
  }

  async resendConfirmation({
    email,
  }: ResetPasswordParams): Promise<AuthResponse> {
    return this.fetch('/guests/confirmation', {
      method: 'POST',
      body: JSON.stringify({ guest: { email } }),
    });
  }

  async confirmEmail(token: string): Promise<AuthResponse> {
    return this.fetch(`/guests/confirmation?confirmation_token=${token}`, {
      method: 'GET',
    });
  }
}

export const authService = new AuthService();
